import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, IdCard, Info } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PageHero, SectionHead } from "../components/site/PageHero";
import { SectorsGrid } from "../components/site/Sections";
import { ApplicationForm } from "../components/forms/ApplicationForm";
import { TrackStatus } from "../components/forms/TrackStatus";
import { MEMBERSHIP_CATEGORIES } from "../data/content";

const ELIGIBILITY = [
  "Currently employed in the IT, ITES or digital services sector, in any of the sectors listed above.",
  "Permanent, probationary, contract, temporary and outsourced employees may apply.",
  "Freelance and contract technology professionals working in the sector may apply.",
  "Applicants must provide accurate personal and employment details with valid ID proof.",
  "Applicants must agree to abide by the rules and bye-laws of the federation.",
];

const STEPS = ["Submit the online application", "Receive your reference number", "Office verification and review", "Approval and membership number"];

const Eligibility = () => (
  <section id="eligibility" className="scroll-mt-28 py-16 md:py-24">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <SectionHead eyebrow="Who can become a member?" title="Membership eligibility" text="Membership is open to employees working in the following sectors:" />
        <SectorsGrid />
      </div>
      <div className="space-y-6 lg:col-span-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="eligibility-rules">
          <h3 className="font-display text-lg font-bold text-navy">General eligibility</h3>
          <ul className="mt-4 space-y-3">
            {ELIGIBILITY.map((e) => <li key={e} className="flex gap-3 text-sm text-slate-600"><CheckCircle2 className="h-5 w-5 shrink-0 text-leaf" />{e}</li>)}
          </ul>
          <p className="mt-5 flex gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900"><Info className="h-4 w-4 shrink-0" />Final eligibility and admission are subject to the federation's approved membership rules and verification by the office.</p>
        </div>
        <div className="rounded-2xl bg-navy p-7 text-white">
          <h3 className="font-display text-lg font-bold">Membership categories</h3>
          <ul className="mt-4 space-y-2 text-sm text-slate-300">{MEMBERSHIP_CATEGORIES.map((c) => <li key={c} className="flex gap-2"><IdCard className="h-4 w-4 text-emerald-300" />{c}</li>)}</ul>
        </div>
      </div>
    </div>
  </section>
);

export default function Membership() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "track" ? "track" : "apply";
  return (
    <>
      <PageHero eyebrow="Membership" title="Join the Federation" subtitle="Become part of an organised voice for IT employees. Apply online and track your application anytime." image="/images/lake-palms.jpg" testId="membership-hero" />
      <section className="border-b border-slate-200 bg-white">
        <div className="container-x grid gap-4 py-8 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-royal font-mono text-sm font-bold text-white">{i + 1}</span><span className="text-sm font-semibold text-navy">{s}</span></div>
          ))}
        </div>
      </section>
      <section id="apply" className="scroll-mt-28 bg-slate-100/70 py-16 md:py-20">
        <div className="container-x">
          <Tabs value={tab} onValueChange={(v) => setParams(v === "track" ? { tab: "track" } : {})}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <TabsList className="h-12 bg-white p-1 shadow-sm">
                <TabsTrigger value="apply" className="h-10 px-5 data-[state=active]:bg-royal data-[state=active]:text-white" data-testid="tab-apply">Apply Online</TabsTrigger>
                <TabsTrigger value="track" className="h-10 px-5 data-[state=active]:bg-royal data-[state=active]:text-white" data-testid="tab-track">Track Application</TabsTrigger>
              </TabsList>
              <a href="/membership-form" target="_blank" rel="noreferrer" className="btn-outline" data-testid="download-membership-form-button"><Download className="h-4 w-4" /> DOWNLOAD MEMBERSHIP FORM</a>
            </div>
            <TabsContent value="apply" className="mt-8"><ApplicationForm /></TabsContent>
            <TabsContent value="track" className="mt-8"><TrackStatus /></TabsContent>
          </Tabs>
        </div>
      </section>
      <Eligibility />
    </>
  );
}
